import React, { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";
import { makeStyles } from "tss-react/mui";

import { setPaneTab, showDetails } from "@dashboard/devices/store";
import { useReleaseFiles } from "@dashboard/downloads/hooks/useReleaseFiles";
import { ActivateDeviceStep } from "@dashboard/enrollment/components/ActivateDeviceStep";
import { DownloadImageStep } from "@dashboard/enrollment/components/DownloadImageStep";
import { EnrollDeviceStep } from "@dashboard/enrollment/components/EnrollDeviceStep";
import { EnrollDeviceUsing6TapsStep } from "@dashboard/enrollment/components/EnrollDeviceUsing6TapsStep";
import { EnrollmentTypeSelectionStep } from "@dashboard/enrollment/components/EnrollmentTypeSelectionStep";
import { GetInTouchStep } from "@dashboard/enrollment/components/GetInTouchStep";
import { SelectDeviceTypeStep } from "@dashboard/enrollment/components/SelectDeviceTypeStep";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { LoadingBox } from "@shared/Loading";

import { codenameParam, resolveWizardStep, toWizardParams, versionParam } from "./EnrollmentPage.utils";

export type EnrollmentPath = "install_android_os" | "install_mdm" | "enroll_existing" | "build_custom";

const useStyles = makeStyles()(() => ({
    page: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
    },
    stepArea: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        width: "100%",
    },
}));

type StepProps = {
    onSkip: () => void;
    onBack: () => void;
    onNext: () => void;
    data: {
        releaseFile: API.ReleaseFile | null;
        releaseFiles: ReturnType<typeof useReleaseFiles>;
        chosenCodename: string | null;
        chosenVersion: string | null;
        onReleaseFileSelect: (releaseFile: API.ReleaseFile) => void;
        onGoToGroupEnrollment: () => void;
        onGoToDevicesEnrollment: () => void;
        onGoToDevices: () => void;
        onContactUs: () => void;
    };
};

type EnrollmentStep = {
    key: string;
    requiresReleaseFile?: boolean;
    render: (props: StepProps) => React.ReactNode;
};

type EnrollmentFlow = EnrollmentStep[];

type EnrollmentFlows = Record<EnrollmentPath, EnrollmentFlow>;

// The step keys are part of the page URL (`?step=download-flash`), so renaming one breaks existing links to it.
const flows: EnrollmentFlows = {
    install_android_os: [
        {
            key: "image-selection",
            render: ({ onSkip, onBack, data }) => (
                <SelectDeviceTypeStep
                    onSkip={onSkip}
                    onBack={onBack}
                    releaseFiles={data.releaseFiles}
                    initialCodename={data.chosenCodename}
                    initialVersion={data.chosenVersion}
                    onContinue={data.onReleaseFileSelect}
                />
            ),
        },
        {
            key: "download-flash",
            requiresReleaseFile: true,
            render: ({ onSkip, onBack, onNext, data }) => (
                <DownloadImageStep onSkip={onSkip} onBack={onBack} onContinue={onNext} selectedReleaseFile={data.releaseFile} />
            ),
        },
        {
            key: "activate-device",
            render: ({ onSkip, onBack, data }) => (
                <ActivateDeviceStep onSkip={onSkip} onBack={onBack} onContinue={data.onGoToDevices} />
            ),
        },
    ],

    install_mdm: [
        {
            key: "six-taps",
            render: ({ onSkip, onBack, data }) => (
                <EnrollDeviceUsing6TapsStep onSkip={onSkip} onBack={onBack} onContinue={data.onGoToGroupEnrollment} />
            ),
        },
    ],

    enroll_existing: [
        {
            key: "enroll-existing",
            render: ({ onBack, data }) => <EnrollDeviceStep onBack={onBack} onContinue={data.onGoToDevicesEnrollment} />,
        },
    ],

    build_custom: [
        {
            key: "build-custom",
            render: ({ onBack, data }) => <GetInTouchStep onBack={onBack} onContact={data.onContactUs} />,
        },
    ],
};

// Marks a history entry the wizard pushed itself, so its Back button can pop that entry instead of pushing another one.
type WizardHistoryState = {
    fromWizard?: boolean;
};

const EnrollmentPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { data: workspace } = useCurrentWorkspace();
    const releaseFiles = useReleaseFiles();

    const chosenCodename = searchParams.get(codenameParam);
    const chosenVersion = searchParams.get(versionParam);
    const wizardStep = resolveWizardStep(flows, searchParams, releaseFiles);
    const current = wizardStep.kind === "step" ? wizardStep : null;
    const redirectSearch = wizardStep.kind === "redirect" ? wizardStep.search : null;

    const goTo = (params: URLSearchParams) => {
        setSearchParams(params, { state: { fromWizard: true } satisfies WizardHistoryState });
    };

    const handleCancel = () => {
        goTo(new URLSearchParams());
    };

    const handleBack = () => {
        if ((location.state as WizardHistoryState | null)?.fromWizard) {
            void navigate(-1);
            return;
        }
        const previousStepKey = current && current.index > 0 ? current.flow[current.index - 1].key : null;
        setSearchParams(toWizardParams(previousStepKey, searchParams), { replace: true });
    };

    const handleContinue = () => {
        const nextStep = current?.flow[current.index + 1];
        if (nextStep) {
            goTo(toWizardParams(nextStep.key, searchParams));
        }
    };

    const handlePathSelect = (path: EnrollmentPath) => {
        goTo(toWizardParams(flows[path][0].key, new URLSearchParams()));
    };

    const handleReleaseFileSelect = (selectedFile: API.ReleaseFile) => {
        const nextStep = current?.flow[current.index + 1];
        if (nextStep) {
            const releaseFileParams = new URLSearchParams({
                [codenameParam]: selectedFile.codename,
                [versionParam]: selectedFile.version,
            });
            goTo(toWizardParams(nextStep.key, releaseFileParams));
        }
    };

    const handleGoToGroupEnrollment = () => {
        const defaultGroupId = workspace?.defaultGroupId;

        if (defaultGroupId) {
            dispatch(setPaneTab("ENROLLMENT"));
            dispatch(
                showDetails({
                    type: "group",
                    selectedId: defaultGroupId,
                }),
            );
        }

        navigate("/dashboard/groups?openEnrollmentModal=true");
    };

    const handleGoToDevicesEnrollment = () => {
        navigate("/dashboard/devices?openEnrollmentModal=true");
    };

    const handleGoToDevices = () => {
        navigate("/dashboard/devices");
    };

    const handleContactUs = () => {
        window.open("https://emteria.com/lp/demo", "_blank");
    };

    const renderStep = () => {
        if (wizardStep.kind === "type-selection") {
            return <EnrollmentTypeSelectionStep onSelect={handlePathSelect} />;
        }

        if (!current) {
            return <LoadingBox size="page" />;
        }

        return current.step.render({
            onSkip: handleCancel,
            onBack: handleBack,
            onNext: handleContinue,
            data: {
                releaseFile: current.releaseFile,
                releaseFiles,
                chosenCodename,
                chosenVersion,
                onReleaseFileSelect: handleReleaseFileSelect,
                onGoToGroupEnrollment: handleGoToGroupEnrollment,
                onGoToDevicesEnrollment: handleGoToDevicesEnrollment,
                onGoToDevices: handleGoToDevices,
                onContactUs: handleContactUs,
            },
        });
    };

    useEffect(() => {
        if (redirectSearch !== null) {
            setSearchParams(redirectSearch, { replace: true });
        }
    }, [redirectSearch, setSearchParams]);

    return (
        <PageContainer className={classes.page}>
            <Box className={classes.stepArea}>
                <ExpiredWrapper>{renderStep()}</ExpiredWrapper>
            </Box>
        </PageContainer>
    );
};

export default withMetaDescription(EnrollmentPage, {
    title: i18next.t("pages:enrollmentPage.metaTitle"),
});
